import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from '../apiClient';
import { useLanguage } from '../i18n/LanguageContext';
import { normalizeRole, roleLabel } from '../utils/userLocale';
import { hasTenantModule } from '../utils/tenantModules';
import { isBarPubUser } from '../utils/businessType';
import './OverviewCommandPanel.css';

const BRANCH_STORAGE_KEY = 'cbonline.overviewBranchFilter';

export function readOverviewBranchFilter() {
  try {
    return window.sessionStorage.getItem(BRANCH_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function branchQuerySuffix(branchId) {
  if (!branchId) return '';
  const id = Number(branchId);
  if (!Number.isFinite(id) || id <= 0) return '';
  return `?branchId=${id}`;
}

/** Only owners may pass a branch filter to the API; others rely on their assigned branch server-side. */
export function branchIdForApi(user, ownerBranchId = '') {
  return normalizeRole(user?.role) === 'owner' ? ownerBranchId || '' : '';
}

export function branchQueryForUser(user, ownerBranchId = '') {
  return branchQuerySuffix(branchIdForApi(user, ownerBranchId));
}

function apiWithBranch(path, branchId) {
  return apiJson(`${path}${branchQuerySuffix(branchId)}`);
}

const OverviewCommandPanel = ({ user, onNavigate, branchId, onBranchChange }) => {
  const { lang, t } = useLanguage();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const canManageTeam = isOwner && hasTenantModule(user, 'users');

  const [branches, setBranches] = useState([]);
  const [branchSearch, setBranchSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [teamRoleFilter, setTeamRoleFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamErr, setTeamErr] = useState('');

  const go = useCallback((key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  }, [onNavigate]);

  useEffect(() => {
    if (!isOwner) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiJson('/owner/branches');
        if (!cancelled) setBranches(data.branches || []);
      } catch {
        if (!cancelled) setBranches([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  const loadTeam = useCallback(async () => {
    if (!canManageTeam) return;
    setTeamLoading(true);
    setTeamErr('');
    try {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      if (teamSearch.trim()) params.set('q', teamSearch.trim());
      const qs = params.toString();
      const data = await apiJson(`/team/users${qs ? `?${qs}` : ''}`);
      setUsers(data.users || []);
    } catch (e) {
      setUsers([]);
      setTeamErr(e.message || t('overview.teamLoadError'));
    } finally {
      setTeamLoading(false);
    }
  }, [canManageTeam, branchId, teamSearch]);

  useEffect(() => {
    if (!canManageTeam) return undefined;
    const t = window.setTimeout(() => {
      void loadTeam();
    }, 280);
    return () => window.clearTimeout(t);
  }, [canManageTeam, loadTeam]);

  const filteredBranches = useMemo(() => {
    const q = branchSearch.trim().toLowerCase();
    if (!q) return branches;
    return branches.filter((b) => String(b.name || '').toLowerCase().includes(q));
  }, [branches, branchSearch]);

  const filteredUsers = useMemo(() => {
    let list = [...users];
    if (teamRoleFilter === 'staff') {
      list = list.filter((u) => normalizeRole(u.role) === 'employee');
    } else if (teamRoleFilter === 'owner') {
      list = list.filter((u) => normalizeRole(u.role) === 'owner');
    }
    return list;
  }, [users, teamRoleFilter]);

  const handleBranchSelect = (value) => {
    const next = value || '';
    try {
      if (next) window.sessionStorage.setItem(BRANCH_STORAGE_KEY, next);
      else window.sessionStorage.removeItem(BRANCH_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent('cbonline:branch-filter', { detail: { branchId: next } }));
    onBranchChange?.(next);
  };

  const activeBranchName =
    branchId && branches.length
      ? branches.find((b) => String(b.id) === String(branchId))?.name || t('overview.selectedBranch')
      : t('overview.allBranches');

  if (!isOwner) {
    return null;
  }

  return (
    <section className="overview-cmd" aria-label={t('overview.ariaLabel')}>
      <div className="overview-cmd__head">
        <div>
          <h2 className="overview-cmd__title">{t('overview.title')}</h2>
          <p className="overview-cmd__sub">
            {isBarPubUser(user) ? t('overview.subBar') : t('overview.sub')}{' '}
            <strong>{activeBranchName}</strong>.
          </p>
        </div>
        <div className="overview-cmd__head-actions">
          <button type="button" className="overview-cmd__btn overview-cmd__btn--ghost" onClick={() => go('branches-list')}>
            {t('overview.manageBranches')}
          </button>
          {canManageTeam ? (
            <button type="button" className="overview-cmd__btn overview-cmd__btn--primary" onClick={() => go('users-add')}>
              {t('overview.addTeam')}
            </button>
          ) : null}
        </div>
      </div>

      <div className="overview-cmd__filters" role="search">
        <label className="overview-cmd__field">
          <span className="overview-cmd__label">{t('overview.searchBranch')}</span>
          <input
            type="search"
            className="overview-cmd__input"
            placeholder={t('overview.searchBranchPlaceholder')}
            value={branchSearch}
            onChange={(e) => setBranchSearch(e.target.value)}
          />
        </label>
        <label className="overview-cmd__field">
          <span className="overview-cmd__label">{t('overview.dashboardBranch')}</span>
          <select
            className="overview-cmd__input"
            value={branchId || ''}
            onChange={(e) => handleBranchSelect(e.target.value)}
          >
            <option value="">{t('overview.allBranchesCombined')}</option>
            {filteredBranches.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
                {!b.isActive ? ` ${t('overview.inactive')}` : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="overview-cmd__field overview-cmd__field--actions">
          <span className="overview-cmd__label">&nbsp;</span>
          <button type="button" className="overview-cmd__btn overview-cmd__btn--secondary" onClick={() => handleBranchSelect('')}>
            {t('overview.clearFilter')}
          </button>
        </div>
      </div>

      {canManageTeam ? (
        <>
          <div className="overview-cmd__filters overview-cmd__filters--team">
            <label className="overview-cmd__field">
              <span className="overview-cmd__label">{t('overview.searchTeam')}</span>
              <input
                type="search"
                className="overview-cmd__input"
                placeholder={t('overview.searchTeamPlaceholder')}
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
              />
            </label>
            <label className="overview-cmd__field">
              <span className="overview-cmd__label">{t('overview.role')}</span>
              <select
                className="overview-cmd__input"
                value={teamRoleFilter}
                onChange={(e) => setTeamRoleFilter(e.target.value)}
              >
                <option value="all">{t('overview.allRoles')}</option>
                <option value="staff">{t('overview.staffOnly')}</option>
                <option value="owner">{t('overview.ownersOnly')}</option>
              </select>
            </label>
            <div className="overview-cmd__field overview-cmd__field--actions">
              <span className="overview-cmd__label">&nbsp;</span>
              <button type="button" className="overview-cmd__btn overview-cmd__btn--ghost" onClick={() => go('users-list')}>
                {t('overview.fullTeam')}
              </button>
            </div>
          </div>

          <div className="overview-cmd__team">
            <div className="overview-cmd__team-head">
              <h3 className="overview-cmd__team-title">{t('overview.teamGlance')}</h3>
              <span className="overview-cmd__team-count">
                {teamLoading
                  ? t('overview.loading')
                  : t('overview.memberCount', { count: filteredUsers.length })}
              </span>
            </div>
            {teamErr ? <p className="overview-cmd__team-err">{teamErr}</p> : null}
            <div className="overview-cmd__table-wrap">
              <table className="overview-cmd__table">
                <thead>
                  <tr>
                    <th>{t('overview.colName')}</th>
                    <th>{t('overview.colEmail')}</th>
                    <th>{t('overview.colBranch')}</th>
                    <th>{t('overview.colRole')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {!teamLoading && filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="overview-cmd__empty">
                        {t('overview.noTeamMatch')}{' '}
                        <button type="button" className="overview-cmd__link" onClick={() => go('users-add')}>
                          {t('overview.addSomeone')}
                        </button>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.slice(0, 12).map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{row.email}</td>
                        <td>{row.branchName || '—'}</td>
                        <td>
                          <span className={`overview-cmd__role overview-cmd__role--${normalizeRole(row.role)}`}>
                            {roleLabel(row.role, lang)}
                          </span>
                        </td>
                        <td className="overview-cmd__row-actions">
                          <button type="button" className="overview-cmd__link" onClick={() => go('users-list')}>
                            {t('overview.manage')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {filteredUsers.length > 12 ? (
              <button type="button" className="overview-cmd__more" onClick={() => go('users-list')}>
                {t('overview.viewAllTeam', { count: filteredUsers.length })}
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default OverviewCommandPanel;

